/**
 * BgGradientGL
 * WebGL fragment shader gradient — bridge light panels drifting horizontally.
 *
 * Usage:
 *   new BgGradientGL(element)
 *   new BgGradientGL(element, { accentColour: false })
 *
 * Tuning constants (all near the top of the fragment shader source):
 *   SPEED       — rad/s for local sinusoidal drift
 *   AMPLITUDE   — fraction of width each zone drifts
 *   CYCLE_SPEED — units/s for global colour cycling
 *   SIGMA       — Gaussian width of each colour zone
 *   FADE_BASE / FADE_AMP / FADE_SPEED — vertical fade oscillation
 */
export default class BgGradientGL {
  constructor(container, { accentColour = true } = {}) {
    if (!container) return;
    this.container = container;
    this.accentColour = accentColour;

    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = 'display:block;width:100%;height:100%';
    this.container.appendChild(this.canvas);

    const gl = this.canvas.getContext('webgl');
    if (!gl) { this.canvas.remove(); return; }
    this.gl = gl;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.raf = null;
    this.startTime = null;
    this.timeOffset = Math.random() * 1000; // random point in the animation cycle
    this.frameCount = 0;
    this.paused = false;

    this._build();
    if (!this.program) { this.canvas.remove(); return; }

    this._resize();

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this._resize(), 150);
    });

    // Pause the RAF loop when scrolled off-screen
    this.observer = new IntersectionObserver(entries => {
      this.paused = !entries[0].isIntersecting;
      if (!this.paused && !this.reducedMotion && this.raf === null) this._start();
    }, { threshold: 0 });
    this.observer.observe(this.container);

    // Pause when tab is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.paused = true;
      } else if (!this.reducedMotion) {
        this.paused = false;
        if (this.raf === null) this._start();
      }
    });

    if (this.reducedMotion) {
      this._render(this.timeOffset);
    } else {
      this._start();
    }
  }

  _build() {
    const gl = this.gl;

    const VS = `
      attribute vec2 aPosition;
      void main() {
        gl_Position = vec4(aPosition, 0.0, 1.0);
      }
    `;

    // --- Tuning constants are at the top of the shader ---
    const FS = `
      precision mediump float;
      uniform vec2  uResolution;
      uniform float uTime;

      // ── Tuning ──────────────────────────────────────────────
      const float SPEED       = 1.00;   // rad/s for local sinusoidal drift
      const float AMPLITUDE   = 0.17;   // fraction of width for local drift
      const float CYCLE_SPEED = 0.04;  // units/s for global leftward cycle
      const float SIGMA       = 0.20;   // Gaussian zone width
      const float FADE_BASE   = 2.00;   // base vertical fade exponent
      const float FADE_AMP    = 0.35;   // oscillation amplitude
      const float FADE_SPEED  = 0.04;   // rad/s for fade oscillation
      // ────────────────────────────────────────────────────────

      // 10 colours cycling: orange → pink → magenta → purple → indigo → blue → back around
      vec3 zoneColour(int i) {
        if (i == 0) return vec3(1.000, 0.333, 0.000); // #ff5500  orange
        if (i == 1) return vec3(1.000, 0.125, 0.376); // #ff2060  orange-pink
        if (i == 2) return vec3(0.878, 0.082, 0.416); // #e0156a  warm pink
        if (i == 3) return vec3(0.753, 0.094, 0.431); // #c0186e  magenta
        if (i == 4) return vec3(0.580, 0.141, 0.690); // #9424b0  magenta-purple
        if (i == 5) return vec3(0.482, 0.208, 0.816); // #7b35d0  purple
        if (i == 6) return vec3(0.267, 0.157, 0.749); // #4428bf  blue-purple
        if (i == 7) return vec3(0.102, 0.086, 0.600); // #1a1699  deep indigo
        if (i == 8) return vec3(0.200, 0.063, 0.490); // #33107d  violet-dark
                    return vec3(0.820, 0.220, 0.100); // #d13819  red-orange (bridge back)
      }

      // Zones drift locally (sinusoidal, opposing directions) and cycle globally (linear wrap)
      float zoneX(int i, float t) {
        // CYCLE: 10 zones spread across 2.4 units (-0.7 → 1.7), wrapping with mod
        float spacing = 2.4 / 10.0;
        float base = mod(-0.7 + float(i) * spacing + t * CYCLE_SPEED, 2.4) - 0.7;
        // LOCAL oscillation — adjacent zones opposing
        float phase = float(i) * 0.897;
        float dir   = (mod(float(i), 2.0) < 1.0) ? 1.0 : -1.0;
        return base + AMPLITUDE * sin(t * SPEED + phase) * dir;
      }

      void main() {
        vec2 uv = gl_FragCoord.xy / uResolution;
        // uv.y: 0 = canvas bottom, 1 = canvas top (WebGL convention)

        // Vertical fade — exponent oscillates so the bright band breathes vertically
        float fadeExp = FADE_BASE + FADE_AMP * sin(uTime * FADE_SPEED);
        float vFade   = pow(uv.y, fadeExp);

        // Accumulate Gaussian-weighted colour zones
        vec3  col    = vec3(0.0);
        float totalW = 0.0;

        for (int i = 0; i < 10; i++) {
          float cx = zoneX(i, uTime);
          float d  = uv.x - cx;
          float w  = exp(-d * d / (2.0 * SIGMA * SIGMA));
          col    += w * zoneColour(i);
          totalW += w;
        }

        col /= max(totalW, 0.001);
        col *= vFade;

        gl_FragColor = vec4(col, 1.0);
      }
    `;

    const vs = this._compile(gl.VERTEX_SHADER, VS);
    const fs = this._compile(gl.FRAGMENT_SHADER, FS);
    if (!vs || !fs) return;

    this.program = gl.createProgram();
    gl.attachShader(this.program, vs);
    gl.attachShader(this.program, fs);
    gl.linkProgram(this.program);

    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      console.warn('BgGradientGL: link failed —', gl.getProgramInfoLog(this.program));
      this.program = null;
      return;
    }

    // Fullscreen quad (two triangles as a strip)
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);

    gl.useProgram(this.program);

    const pos = gl.getAttribLocation(this.program, 'aPosition');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

    this.uResolution = gl.getUniformLocation(this.program, 'uResolution');
    this.uTime       = gl.getUniformLocation(this.program, 'uTime');
  }

  _compile(type, src) {
    const gl = this.gl;
    const sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      console.warn('BgGradientGL: compile failed —', gl.getShaderInfoLog(sh));
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  _resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width  = Math.round(this.container.offsetWidth  * dpr);
    this.canvas.height = Math.round(this.container.offsetHeight * dpr);
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  _start() {
    if (this.raf !== null) return;
    const tick = (now) => {
      if (this.paused) { this.raf = null; return; }
      if (this.startTime === null) this.startTime = now;
      this._render((now - this.startTime) * 0.001 + this.timeOffset);
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  _render(t) {
    const gl = this.gl;
    gl.uniform2f(this.uResolution, this.canvas.width, this.canvas.height);
    gl.uniform1f(this.uTime, t);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    this.frameCount++;

    // Sample dominant colour on frame 1 and every 10 frames thereafter
    if (this.accentColour && (this.frameCount === 1 || this.frameCount % 10 === 0)) {
      const x = Math.round(this.canvas.width  / 2);
      const y = Math.round(this.canvas.height * 0.9);
      const px = new Uint8Array(4);
      gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
      document.documentElement.style.setProperty(
        '--color-gradient-accent',
        `rgb(${px[0]},${px[1]},${px[2]})`
      );
    }
  }
}
